/**
 * Compact "Uitvoering" editor for a collector on the project Dashboard:
 * op-druk toggle + datum + installateur, saved in one call via
 * `patchCollectorOpDruk`. Mirrors the legacy CollectorenTab op-druk popover, but
 * inline in the expanded dashboard row instead of a floating popover.
 *
 * Gated by the caller with `collector_toggle` (same as the legacy toggle).
 */
import { useState } from 'react';
import type { Collector } from '@/types/odoo';
import { useProjectViewStore } from '@/stores/projectView';
import { patchCollectorOpDruk } from '@/api/installer';

function todayStr(): string {
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

export function CollectorUitvoeringEditor({
  collector,
  projectId,
  installerNames,
  onUpdated,
}: {
  collector: Collector;
  projectId: number;
  installerNames: Record<string, string>;
  onUpdated?: () => void;
}) {
  const [opDruk, setOpDruk] = useState(!!collector.x_studio_collector_op_druk);
  const [date, setDate] = useState(
    collector.x_studio_collector_op_druk_datum
      ? String(collector.x_studio_collector_op_druk_datum).slice(0, 10)
      : todayStr(),
  );
  const [installerId, setInstallerId] = useState<string>(
    collector.x_studio_collector_installateur?.[0]
      ? String(collector.x_studio_collector_installateur[0])
      : '',
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const installerEntries = Object.entries(installerNames);
  const dirty = () => setSaved(false);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      const instId = opDruk && installerId ? Number(installerId) : undefined;
      const datum = opDruk ? date : undefined;
      await patchCollectorOpDruk(projectId, collector.id, opDruk, instId, datum);

      // Optimistic store update so the table row (op-druk ✓ + datum) reflects at once.
      const store = useProjectViewStore.getState();
      if (store.collectors) {
        useProjectViewStore.setState({
          collectors: store.collectors.map((c) =>
            c.id === collector.id
              ? {
                  ...c,
                  x_studio_collector_op_druk: opDruk,
                  x_studio_collector_op_druk_datum: opDruk ? date : false,
                  x_studio_collector_installateur: instId
                    ? [instId]
                    : opDruk
                      ? c.x_studio_collector_installateur
                      : [],
                }
              : c,
          ),
        });
      }
      setSaved(true);
      onUpdated?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Opslaan mislukt');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 px-4 py-3">
      <div className="flex flex-wrap items-end gap-4">
        <div>
          <span className="block text-[11px] font-extrabold uppercase tracking-wide text-td-muted mb-1">Op druk</span>
          <button
            type="button"
            role="switch"
            aria-checked={opDruk}
            aria-label="Op druk aan/uit"
            onClick={() => { setOpDruk((v) => !v); dirty(); }}
            className={`relative inline-flex h-6 w-11 shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-td-blue focus:ring-offset-2 cursor-pointer ${opDruk ? 'bg-td-green' : 'bg-gray-300'}`}
          >
            <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${opDruk ? 'translate-x-5' : 'translate-x-0'}`} />
          </button>
        </div>

        {opDruk && (
          <div>
            <label htmlFor={`uitv-datum-${collector.id}`} className="block text-[11px] font-extrabold uppercase tracking-wide text-td-muted mb-1">
              Datum
            </label>
            <input
              type="date"
              id={`uitv-datum-${collector.id}`}
              value={date}
              onChange={(e) => { setDate(e.target.value); dirty(); }}
              className="px-2 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-td-blue/40 bg-white"
            />
          </div>
        )}

        {opDruk && installerEntries.length > 0 && (
          <div>
            <label htmlFor={`uitv-inst-${collector.id}`} className="block text-[11px] font-extrabold uppercase tracking-wide text-td-muted mb-1">
              Installateur
            </label>
            <select
              id={`uitv-inst-${collector.id}`}
              value={installerId}
              onChange={(e) => { setInstallerId(e.target.value); dirty(); }}
              className="px-2 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-td-blue/40 bg-white"
            >
              <option value="">-- Selecteer --</option>
              {installerEntries.map(([id, name]) => (
                <option key={id} value={id}>{name}</option>
              ))}
            </select>
          </div>
        )}

        <button
          type="button"
          onClick={handleSave}
          disabled={saving}
          className="px-3 py-1.5 text-sm font-semibold text-white bg-td-blue rounded-lg hover:bg-td-blue/90 transition disabled:opacity-50"
        >
          {saving ? 'Opslaan…' : 'Uitvoering opslaan'}
        </button>
        {saved && <span className="text-xs font-semibold text-td-green">✓ Opgeslagen</span>}
      </div>
      {error && <p className="text-xs text-red-500 mt-2">{error}</p>}
    </div>
  );
}
